import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { App } from '@capacitor/app';

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

// Build WebSocket URL from API URL
function getSocketURL(): string {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
  // Remove /api suffix
  const baseUrl = apiUrl.replace(/\/api$/, '');
  console.log('🔌 Socket URL:', baseUrl);
  return baseUrl;
}

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      // Disconnect when user logs out
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socketUrl = getSocketURL();
    console.log('🔌 Connecting to Socket.IO at:', socketUrl);

    const newSocket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Infinite reconnect attempts
      timeout: 20000,
      autoConnect: true
    });

    newSocket.on('connect', () => {
      console.log('✅ Socket connected, ID:', newSocket.id);
      setConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected, reason:', reason);
      setConnected(false);
      
      // Force reconnect if server disconnected
      if (reason === 'io server disconnect') {
        console.log('🔄 Server disconnected, reconnecting...');
        newSocket.connect();
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
      setConnected(true);
      
      // Trigger offline sync nach Socket-Reconnect
      window.dispatchEvent(new CustomEvent('socket-reconnected'));
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Reconnect attempt', attemptNumber);
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('❌ Reconnect error:', error.message);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ Reconnect failed after all attempts');
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
    });

    newSocket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });

    // Handle visibility change (app goes to background/foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('📱 App visible, checking socket connection...');
        if (!newSocket.connected) {
          console.log('🔄 Reconnecting socket...');
          newSocket.connect();
        }
      }
    };

    // Handle network online/offline
    const handleOnline = () => {
      console.log('🌐 Network online, reconnecting socket...');
      if (!newSocket.connected) {
        newSocket.connect();
      }
    };

    const handleOffline = () => {
      console.log('📴 Network offline');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Capacitor App State Listener (für native Android App)
    let appStateListener: any;
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('📱 [Socket] App state changed, isActive:', isActive);
      if (isActive) {
        console.log('🔄 [Socket] App became active, checking socket connection...');
        console.log('🔌 [Socket] Current connected state:', newSocket.connected);
        
        // Längere Verzögerung für Netzwerk-Stabilisierung
        setTimeout(() => {
          console.log('🔌 [Socket] After delay - Connected:', newSocket.connected);
          
          // Immer disconnecten und neu connecten für saubere Verbindung
          if (newSocket.connected) {
            console.log('🔄 [Socket] Forcing clean reconnect...');
            newSocket.disconnect();
          }
          
          // Token-Check vor Reconnect
          const currentToken = localStorage.getItem('accessToken');
          if (!currentToken) {
            console.error('❌ [Socket] No access token found, cannot reconnect');
            return;
          }
          
          // Fresh connect mit aktuellem Token
          newSocket.auth = { token: currentToken };
          newSocket.connect();
          console.log('✅ [Socket] Reconnect triggered');
        }, 1000); // 1 Sekunde für bessere Netzwerk-Stabilität
        
        // Trigger custom event für UI-Refresh
        window.dispatchEvent(new CustomEvent('app-resumed'));
      } else {
        console.log('📴 [Socket] App went to background');
      }
    }).then(listener => {
      appStateListener = listener;
    });

    setSocket(newSocket);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (appStateListener) {
        appStateListener.remove();
      }
      newSocket.close();
    };
  }, [user]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
